import type { DriveStep, Side } from 'driver.js';

const at = (
  focus: string,
  title: string,
  description: string,
  side: Side = 'bottom'
): DriveStep => ({
  element: `[data-focus="${focus}"]`,
  popover: { title, description, side, align: 'start' },
});

const CHROME: DriveStep[] = [
  at(
    'preview',
    'Live preview',
    'Your project as it will appear in the feed. It fills in as you go, so you can see what a maker sees.',
    'left'
  ),
  at(
    'progress',
    'Six steps',
    'Click any step you have finished to go back to it. A step unlocks once you continue past it.',
    'left'
  ),
  at(
    'continue',
    'Saved as you go',
    'Continue saves this step to your draft before moving on — nothing is lost if you stop halfway.',
    'top'
  ),
  at(
    'exit',
    'Leave whenever',
    'Your draft lives on the server. Reopening this page picks up exactly where you left off.',
    'bottom'
  ),
  // The wizard's furniture rather than the step's own fields — useTour hangs the
  // Skip button off this.
].map((beat) => ({ ...beat, data: { nav: true } }));

const CHOOSE_TYPE: DriveStep[] = [
  at(
    'listing-type',
    'One piece, or several?',
    'One board makes a single project. Two or more — a layered sign, a box — is a multi-workpiece project, and you build each piece in turn. This is set once and cannot be changed later.'
  ),
];

const BASICS: DriveStep[] = [
  at(
    'title',
    'Name your project',
    "The first thing a maker sees. Be specific — 'Walnut phone dock' beats 'my project'."
  ),
  at(
    'description',
    'Say what it is',
    'The material, the finish, anything someone should know before they start cutting.'
  ),
  at(
    'category',
    'Pick a category',
    'Decides where your project turns up when people browse the site.'
  ),
  at(
    'difficulty',
    'How hard is it?',
    'Required — it sets expectations before someone commits time and material.'
  ),
  at(
    'tags',
    'Tags',
    'How people find this in search. Type a word and press Enter to add it.',
    'top'
  ),
];

const FILES: DriveStep[] = [
  at(
    'dropzone',
    'Drop everything in at once',
    'G-code, photos, CAD, PDFs — any format. We sort them into the right sections below.'
  ),
  at(
    'pick',
    'Or browse for them',
    'Select folder walks the whole folder for you, so nested files are not missed.'
  ),
  at(
    'tips',
    'Worth doing',
    'Projects with photos and a setup PDF get downloaded far more often.',
    'left'
  ),
  at(
    'images',
    'Photos',
    'Click any photo to make it the thumbnail — that is the one the feed shows.'
  ),
  at(
    'controllers',
    'G-code, sorted by controller',
    'We read each file header to tell Masso, BuildBotics and Redline apart. Move a card if we guessed wrong.'
  ),
  at(
    'docs',
    'CAD files and docs',
    'Source files and setup instructions, so makers can open the project in their own CAM.'
  ),
  at(
    'videos',
    'Video links',
    'Paste a YouTube URL. A build video is not a file, so it lives here rather than above.',
    'top'
  ),
];

const TOOLS: DriveStep[] = [
  at(
    'tool-groups',
    'One row per G-code file',
    'Each file is a cut. Tell makers which bit or laser ran it.'
  ),
  at(
    'cut-order',
    'Cut order',
    'Drag a row to change the order the files run in. Uploaded the same cut for two controllers? Filling in the first fills in the rest.',
    'right'
  ),
  at(
    'bit',
    'Pick the bit',
    'Opens the catalog. Choose the bit or laser and give it a spindle speed — both are needed before you can continue.',
    'left'
  ),
];

const JOB_DETAILS: DriveStep[] = [
  at(
    'material-section',
    'Material and size',
    'What the project is made from, and how big the stock needs to be.',
    'right'
  ),
  at(
    'material',
    'Primary material',
    'The main stock. This shows on your project card.'
  ),
  at(
    'jobSize',
    'Read from your G-code',
    'We filled these in from the file, units included. Correct them if your stock differed.'
  ),
  at(
    'setup-section',
    'Machine setup',
    'How the job is cut and where it is zeroed — what a maker needs before touching the machine.',
    'left'
  ),
  at(
    'jobType',
    'Project type',
    'Rotary swaps the size fields to length and diameter. Double sided adds a flip direction below.'
  ),
  at(
    'zZero',
    'Z zero',
    'Where the vertical origin sits. Which options you get depends on the project type.'
  ),
  at(
    'xyDatum',
    'XY origin',
    'Click the corner or the centre of the board you zeroed from.'
  ),
  at(
    'flipDirection',
    'Flip direction',
    'Which way the board turns over between the two sides. G-code cannot say — only you know.'
  ),
  at(
    'software-section',
    'Design software',
    'What you drew and toolpathed it in. We pre-fill whatever the G-code names.',
    'left'
  ),
  at(
    'features-section',
    'Machine features',
    'Say so if the job assumes a tool setter or an automatic tool changer.',
    'left'
  ),
  at(
    'links-section',
    'Where to buy the material',
    'Optional. A link to the exact board is what makes a project repeatable.',
    'right'
  ),
];

const LICENSE: DriveStep[] = [
  at(
    'license-presets',
    'How others can use it',
    'Four levels, most open to most restrictive. Pick one — you can change it later.'
  ),
  at(
    'license-compare',
    'What each one allows',
    'Your pick is the highlighted column. Custom follows whichever Creative Commons license you choose below.'
  ),
  at(
    'license-cc',
    'Or choose a CC license directly',
    'All seven Creative Commons licenses, for when you already know which one you want.',
    'top'
  ),
];

const SIGNOFF: DriveStep[] = [
  at(
    'review',
    'Everything in one place',
    'Everything you picked, as it will be posted. Double-click any field to jump to the step that sets it, with that field highlighted.'
  ),
  at(
    'review-readonly',
    'Greyed cells',
    'These came out of your G-code or follow where your files sit, so there is nothing to set by hand.'
  ),
  at(
    'attachments',
    'Your files',
    'Double-click any card to jump back to Add files with that file in view.'
  ),
  at(
    'review-tools',
    'Bits in cut order',
    'Read-only here. Go back to Tools to change an assignment.'
  ),
  at(
    'continue',
    'Submit for Review',
    'Sends the project to the moderation queue. You will get an email once it is published.',
    'top'
  ),
];

/** The screen after a workpiece finishes. Both cards carry the same anchor, so
 * the copy has to read for either one. */
const WORKPIECE_COMPLETE: DriveStep[] = [
  at(
    'add-workpiece',
    'Another piece?',
    'Each workpiece is a whole project of its own — its own files, tools and job setup — so this walks the six steps again. Nothing carries over.'
  ),
  at(
    'finish-workpieces',
    'Or wrap it up',
    'Finishing takes you to one last screen: name the whole project, check the workpieces over, and submit the set. With only one piece so far, it offers to submit that on its own instead.'
  ),
];

const GROUP_REVIEW: DriveStep[] = [
  at(
    'title',
    'Name the whole project',
    'This is the set, not the piece — what the workpieces make together. Each one keeps its own title.'
  ),
  at(
    'cover',
    'One cover image',
    'The picture the project leads with in search and listings. The workpieces keep their own photos.'
  ),
  at(
    'license-presets',
    'One license, whole set',
    'It applies to every workpiece, so nobody has to read three different sets of terms.'
  ),
  at(
    'projects',
    'Check the workpieces',
    'Edit any of them and come back. A workpiece with something missing is flagged here, and blocks the submit until it is fixed.'
  ),
  at(
    'continue',
    'One submission',
    'The whole set goes to the reviewers together and publishes as one project.',
    'top'
  ),
];

const BY_STEP: DriveStep[][] = [
  [...CHOOSE_TYPE, ...CHROME],
  BASICS,
  FILES,
  TOOLS,
  JOB_DETAILS,
  LICENSE,
  SIGNOFF,
];

/** `stage` is the multi flow's own screen; absent means the six-step walk,
 * whose beats are the same ones single mode uses. */
const tourForStep = (
  step: number,
  stage?: 'interstitial' | 'review'
): DriveStep[] => {
  if (stage === 'interstitial') return WORKPIECE_COMPLETE;
  if (stage === 'review') return GROUP_REVIEW;
  return BY_STEP[step] ?? [];
};

export default tourForStep;
